/**
 * Millisecond clock for countdown UIs aligned with the API server (`server-time-tick`).
 *
 * The clock always advances on a local 1s timer so countdowns tick smoothly; when the shared PvP
 * socket delivers a `server-time-tick`, the local/server drift is re-measured and folded into an
 * offset. That keeps the value server-authoritative without depending on the socket for motion —
 * previously a page where the socket never connected (or stopped ticking) froze the countdown at
 * its mount value.
 *
 * Opt-in via `{ enabled: true }` so the socket/timers are not active app-wide when no countdown needs server time.
 */
import { useEffect, useRef, useState } from 'react'
import { getSharedPvPSocket } from '../socket/pvpSharedSocket'

export type UseServerNowMsOptions = {
  /**
   * When `true`, keeps `serverNowMs` ticking every second, re-aligned to the server on each
   * `server-time-tick`. When `false`, does not subscribe; the returned value stays fixed after mount.
   * @default false
   */
  enabled?: boolean
}

export function useServerNowMs(
  walletAddress: string | null | undefined,
  options?: UseServerNowMsOptions,
): number {
  const enabled = options?.enabled === true
  const [serverNowMs, setServerNowMs] = useState(() => Date.now())
  /** serverNow − Date.now() at the last tick; 0 until the first one arrives. */
  const serverOffsetMsRef = useRef(0)

  useEffect(() => {
    if (!enabled) return

    /** Local motion, independent of the socket — guarantees a 1 Hz countdown everywhere. */
    const intervalId = window.setInterval(() => {
      setServerNowMs(Date.now() + serverOffsetMsRef.current)
    }, 1000)

    if (!walletAddress) {
      return () => window.clearInterval(intervalId)
    }

    const socket = getSharedPvPSocket(walletAddress)
    const onTick = (payload: { serverNow?: number }) => {
      const t = payload?.serverNow
      if (typeof t === 'number' && !Number.isNaN(t)) {
        serverOffsetMsRef.current = t - Date.now()
        setServerNowMs(t)
      }
    }

    socket.on('server-time-tick', onTick)
    return () => {
      socket.off('server-time-tick', onTick)
      window.clearInterval(intervalId)
    }
  }, [walletAddress, enabled])

  return serverNowMs
}
